import { collectionName } from "@/entities/attendanceEvent/attendanceEventMeta"
import { AttendanceEvent } from "@/entities/attendanceEvent/AttendanceEventType"
import { dexie } from "@/firestore/localDatabase"
import { _processAttendanceEvents } from "./_processAttendanceEvents"
import { AttendanceStatus } from "@/types/attendanceStatus"
import { GlobalSettings } from "@/entities/globalSettings/globalSettings"


async function getSortedEventsAtTime(schoolyear_id: string, young_id: string, time: Date) {
   return await dexie
      .table<AttendanceEvent>(collectionName)
      .where("[school_year_id+young_id]")
      .equals([schoolyear_id, young_id])
      .and((doc) => {
         const { from, to } = doc
         const eventContainsTime = from <= time && time <= to
         return eventContainsTime
      })
      .sortBy("from")
}


export async function getLastEventUsedToComputeAttendanceAtTimeForRowCall({
   young_id,
   time,
   schoolyear_id,
}: {
   young_id: string
   time: Date
   schoolyear_id: string
}): Promise<AttendanceEvent | undefined> {
   const events = await getSortedEventsAtTime(schoolyear_id, young_id, time)
   const lastEvent = events[events.length - 1]
   return lastEvent
}


export async function computeAttendanceAtTimeForRowCall({
   young_id,
   time,
   schoolyear_id,
}: {
   young_id: string
   time: Date
   schoolyear_id: string
}): Promise<{ status: AttendanceStatus; expected: boolean }> {
   const lastEvent = await getLastEventUsedToComputeAttendanceAtTimeForRowCall({
      young_id,
      time,
      schoolyear_id,
   })

   const status = lastEvent?.type || "present"
   const expected = status === "present" || lastEvent?.justified || lastEvent?.expected || false

   return { status, expected }
}

export async function computeIfPerfectAttendanceForPeriod({
   young_id,
   schoolyear_id,
   from: periodFrom,
   to: periodTo,
}: {
   young_id: string
   schoolyear_id: string
   from: Date
   to: Date
}) {
   const nbImperfectEvents = await dexie
      .table<AttendanceEvent>(collectionName)
      .where("[school_year_id+young_id]")
      .equals([schoolyear_id, young_id])
      .and((doc) => {
         const { from, to } = doc
         const eventOverlapsPeriod = from <= periodTo && to >= periodFrom
         return eventOverlapsPeriod
      })
      .and((doc) => {
         return doc.type !== "present" && doc.type !== "external"
      })
      .count()

   const isPerfect = nbImperfectEvents === 0

   return isPerfect
}

export async function computeAttendanceForPeriod({
   young_id,
   schoolyear_id,
   from: periodFrom,
   to: periodTo,
}: {
   young_id: string
   schoolyear_id: string
   from: Date
   to: Date
}): Promise<{ status: AttendanceStatus; justified: boolean }> {
   const events = await dexie
      .table<AttendanceEvent>(collectionName)
      .where("[school_year_id+young_id]")
      .equals([schoolyear_id, young_id])
      .and((doc) => {
         const { from, to } = doc
         const eventOverlapsPeriod = from < periodTo && to > periodFrom
         return eventOverlapsPeriod
      })
      .toArray()

   // Process events to get ranges
   const ranges = _processAttendanceEvents(events, periodFrom, periodTo)

   // Determine final status based on ranges
   function determineStatus() {
      if (ranges.some((range) => range.type === "absent")) {
         const status = "absent"
         const justified = ranges.every((range) => range.justified)
         return { status, justified }
      }

      if (ranges.some((range) => range.type === "late")) {
         const status = "late"
         const justified = ranges.every((range) => range.justified)
         return { status, justified }
      }

      if (ranges.some((range) => range.type === "external")) {
         const status = "external"
         const justified = true
         return { status, justified }
      }

      return { status: "present", justified: true }
   }

   const { status, justified } = determineStatus()

   return { status, justified } as any
}


export async function computeAttendanceForQuarters({ 
   youngId,
   day,
   schoolyearId,
   dayQuarters,
   globalSettings,
}: {
   youngId: string
   day: Date
   schoolyearId?: string
   dayQuarters?: { start: Date; end: Date }[]
   globalSettings?: GlobalSettings
   }) {
   
   
      if (!schoolyearId || !globalSettings || !dayQuarters) return {}

      const newStatuses: Record<string, { status: AttendanceStatus; justified: boolean }> = {}

      for (let i = 0; i < dayQuarters.length; i++) {
         const quarter = dayQuarters[i]
         const key = `${day.toISOString()}-${i}`
         const status = await computeAttendanceForPeriod({
            young_id: youngId,
            schoolyear_id: schoolyearId,
            from: quarter.start,
            to: quarter.end,
         })
         newStatuses[key] = status
      }

      return newStatuses
   
}
